import { fetchQuery, graphql } from 'react-relay';
import type { IEnvironment } from 'relay-runtime';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics/utils/fire-operational-event';

import type { LookupVersions } from '../../common/types/lookup';
import { ErrorWithStatusCode } from '../../common/utils/errors/error-with-status-code';
import type {
	fetchVersionsQuery,
	fetchVersionsQuery$data,
} from './__generated__/fetchVersionsQuery.graphql';

export const fetchVersions = async (
	environment: IEnvironment,
	cloudId: string,
	projectIdOrKey: string,
	versionIds: string[],
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<LookupVersions> => {
	try {
		const apiStartTime = performance.now();

		const response: fetchVersionsQuery$data | undefined = await fetchQuery<fetchVersionsQuery>(
			environment,
			graphql`
				query fetchVersionsQuery(
					$cloudId: ID!
					$idOrKey: String!
					$versionIds: [ID!]!
					$first: Int!
				) {
					jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $idOrKey) {
						versions(
							first: $first
							filter: [RELEASED, UNRELEASED, ARCHIVED]
							filterById: { ids: $versionIds, operation: ALLOW }
						) {
							edges {
								node {
									versionId
									name
								}
							}
						}
					}
				}
			`,
			{ cloudId, idOrKey: projectIdOrKey, versionIds, first: versionIds.length },
			{
				networkCacheConfig: { force: false },
				fetchPolicy: 'store-or-network',
			},
		).toPromise();

		const connection = response?.jira_projectByIdOrKey?.versions;

		if (!connection) {
			throw new ErrorWithStatusCode(
				'[fetch-versions service] [fetchVersionsQuery]: Property jira_projectByIdOrKey.versions does not exist.',
				{
					cause: 'FETCH_VERSIONS_MISSING_VERSIONS_PROPERTY',
				},
				'FETCH_VERSIONS_MISSING_VERSIONS_PROPERTY',
			);
		}

		const resolved: LookupVersions = [];

		for (const edge of connection.edges ?? []) {
			const node = edge?.node;
			if (node?.versionId) {
				resolved.push({ id: node.versionId, name: node.name ?? '' });
			}
		}

		const apiLatencyMs = Math.floor(performance.now() - apiStartTime);

		fireOperationalEvent(createAnalyticsEvent, {
			action: 'success',
			actionSubject: 'jiraUiModificationsFetchVersions',
			attributes: {
				versionsAPILatency: apiLatencyMs,
			},
		});

		return resolved;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		if (!err.cause) {
			err.cause = 'FETCH_VERSIONS_UNKNOWN_ERROR';
		}

		throw err;
	}
};
